import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { shareService } from '../../services/sharing';
import { BookOpenIcon, EyeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const SharedEntryView = () => {
  const { token } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedEntry = async () => {
      try {
        const response = await shareService.getSharedEntry(token);
        setEntry(response.data);
        setLoading(false);
      } catch (error) {
        setError('Entry not found or access expired');
        setLoading(false);
      }
    };

    fetchSharedEntry();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">
            Entry Not Found
          </h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <BookOpenIcon className="h-8 w-8 text-primary-600" />
              <span className="text-2xl font-serif font-bold text-leather-brown">
                DiaryVerse
              </span>
            </div>
            <h1 className="text-4xl font-serif font-bold text-ink-black mb-4">
              {entry.entry.title}
            </h1>
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <CalendarIcon className="h-4 w-4" />
                <span>{format(new Date(entry.entry.createdAt), 'MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center space-x-1">
                <EyeIcon className="h-4 w-4" />
                <span>{entry.shareInfo.accessCount} views</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="diary-paper p-8 rounded-xl">
            <div
              className="prose prose-lg max-w-none text-gray-700"
              style={{
                fontFamily: 'Crimson Text, serif',
                lineHeight: '1.8',
                fontSize: '18px'
              }}
              dangerouslySetInnerHTML={{ __html: entry.entry.content }}
            />
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-500">
            <p>Shared via DiaryVerse • {entry.shareInfo.permission} access</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SharedEntryView;
